import React, { useEffect, useRef, useState } from 'react';
import { call, getToken, askStreaming } from './api.js';
import { withReturn, hereNow, needsAccount } from './gate.jsx';
import { track } from './track.js';
/* ⚠️ THE ASK BAR IS THE ONE HOME DRAWS — `SearchBar` in ui.jsx, one component
   and one block of CSS. Al, 24 September 2026: *"you need to make the
   search/ask a question for zaryna like you see on google."* */
import { TabBar, VenueCard, SearchBar, Waveform, useSpeech, ZarynaFace, ZarynaSteps, plainAnswer, MicStatus } from './ui.jsx';
import { SpeakButton, speakAnswer, stopSpeaking } from './speak.jsx';
import { startConversation, myLanguage } from './voice.js';
/* ⚠️ THE ONE MODULE THAT KNOWS WHERE SOMEBODY IS AND WHICH MARKET THAT IS.
   This screen used to parse `eleat.centre` out of localStorage by hand. */
import { readCentre, marketFor, browseMarket, writeCentre, centreFrom } from './place.js';
import { tx, zh } from './i18n.js';
import { goBack } from './nav.js';
import { BookingCard, isBookingAnswer } from './zbook.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * ZARYNA — the Eleater side
 *
 * From `08f_Zaryna.html`: the placeholder "Ask about a restaurant, a dish or
 * tonight's plans", quoted rather than reworded.
 *
 * ⚠️ ONE ROUTE, AND IT IS ELEATER-ONLY. POST /zaryna/chat carries
 * requireType('eleater'). The other seventeen Zaryna routes are the
 * restaurant's — insights, draft-reply, parse-menu, leftovers, cocktail and
 * the rest — and every one of them carries requireType('restaurant'). The
 * gate exists so a restaurateur cannot use the concierge to recommend
 * themselves, which is defect B20. Nothing here reaches those routes.
 *
 * ── SHE ANSWERS, OR SHE ASKS. NEVER BOTH, NEVER NEITHER ─────────────────────
 *
 * The route returns one of three shapes and the screen must handle all three:
 *
 *   { reply, connected, model }                     — an answer
 *   { reply: null, needs: 'location_choice', … }    — "which KT do you mean?"
 *   { reply: null, needs: 'market', question }      — "which city?"
 *
 * ⚠️ RENDERING `reply` WITHOUT CHECKING `needs` WOULD PRINT AN EMPTY BUBBLE.
 * She asked a question and the screen showed nothing — which reads as Zaryna
 * ignoring somebody, and is the worst failure a concierge can have.
 *
 * ⚠️ AND SHE NEVER PICKS AN AREA. "KT" is Kennedy Town and Kwun Tong; she
 * hands back both and the Eleater chooses, exactly as Explore does. The same
 * rule, in two places, because the resolver is the same one.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️⚠️ `const MARKET = 'hong-kong'` WAS HERE AND IT WAS THE WORST PLACE FOR IT.
 * A comment two hundred lines below this used to read *"`market` STAYS AS IT
 * IS… swapping the source is a change to something that already works and Al
 * did not ask for it."*  **He has now asked for it, in writing**, 25 September
 * 2026 — and "already works" was only ever true in Hong Kong. Stand in Calgary
 * and Zaryna searched Hong Kong, so every venue she named and every card she
 * returned was on the other side of the world from the person asking.
 *
 * ⚠️ AND IT IS STILL OURS, NEVER HERS. The point of sending a market at all is
 * that she cannot widen her own reach; that is unchanged. It is now the market
 * the Eleater is actually browsing instead of a constant.
 *
 * ⚠️ WHERE THEY ARE — READ THROUGH `place.js`, WHICH THIS USED TO GO ROUND.
 * It parsed `eleat.centre` out of localStorage itself, which made this the
 * second thing in the app that knew the shape of that object — the exact way
 * the pin bug and the label bug both happened. One module owns it.
 *
 * ⚠️ AND IT NEVER THROWS. Somebody who has not set a location yet has none,
 * and private browsing refuses storage. Both mean the same thing: she works
 * without distance and says so, rather than guessing a number. */
function whereTheyAre() {
  const c = readCentre();
  return (c && Number.isFinite(c.lat) && Number.isFinite(c.lng))
    ? { lat: c.lat, lng: c.lng } : {};
}

/** The market she is allowed to search — null where Eleat has no venues. */
function marketNow() { return browseMarket(readCentre()).market; }   /* the city Home shows */


/* ⚠️ HER FOUR PROMPTS, WORD FOR WORD FROM `08f_Zaryna.html .sugs`.
 * RULE 4 — this copy is Eleat's. Not "Find me dinner", not "What's nearby";
 * these four, in this order, with her apostrophes. */
const SUGGESTIONS = [
  'Plan a date night',
  "I'm craving something",
  "Where's the game on?",
  'Surprise me',
];

/* ⚠️⚠️ THE ROTATING EXAMPLE — `08f_Zaryna.html .ztry`, AND IT WAS NEVER BUILT.
 *
 * Al, 24 September 2026: *"even on Zaryna, we had questions there or sample
 * questions or what she will do and so on and all that. None of it's there."*
 * He is right. `.sugs` — the four short prompts — existed; THIS did not, and
 * it is the thing that actually shows somebody what Zaryna is for, because a
 * real question in real words does what "Surprise me" cannot.
 *
 * ⚠️ AND IT IS A BUTTON, NOT A PLACEHOLDER, WHICH IS THE WHOLE POINT. The
 * locked file's own comment says why, from Al on 28 August: somebody reads
 * "Who has all-you-can-eat thali?", thinks yes — and a placeholder vanishes
 * the moment they touch the field, leaving nothing to tap. So it sits ABOVE
 * the input as a real control and tapping it ASKS. */
const EXAMPLES = [
  'Where should I take my wife for her birthday?',
  'Find me a ribeye steak near me',
  'Who has all-you-can-eat thali?',
  'Which rooftop patios have happy hour?',
  'Where can I watch the game tonight, and what time?',
];
const EXAMPLE_TURN_MS = 3200;

/* one of Eleat's cities named in the question ("Hongkong", "香港", "HK",
   "Calgary", "YYC") — the whole name as words, spaces optional */
/* what people call a city in two or three letters — by its English name, so
   no city's code is typed into this screen (verify_travel.js) */
const SHORT_NAMES = { 'hong kong': ['hk'], calgary: ['yyc'] };
function cityInWords(message, cities) {
  const m = ' ' + String(message || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ') + ' ';
  const tight = m.replace(/\s+/g, '');
  for (const c of cities || []) {
    const names = [c.name_en, c.name_zh, c.code, String(c.code || '').replace(/-/g, ' ')]
      .concat(SHORT_NAMES[String(c.name_en || '').toLowerCase()] || [])
      .filter(Boolean).map((n) => String(n).toLowerCase().trim());
    for (const n of names) {
      if (/[\u3400-\u9fff]/.test(n)) { if (m.includes(n)) return c; continue; }
      const w = n.replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
      if (w.length < 2) continue;
      if (m.includes(' ' + w + ' ')) return c;
      if (w.includes(' ') && w.replace(/\s+/g, '').length >= 6 && tight.includes(w.replace(/\s+/g, ''))) return c;
    }
  }
  return null;
}

export default function Zaryna() {
  /* the conversation, oldest first. Each is { who: 'me' | 'z', … } */
  const [turns, setTurns] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  /* what she found for the question in hand (the stream's 'found' step) */
  const [found, setFound] = useState(null);
  const foundRef = useRef(null);   // the same, readable when her answer finishes
  /* the places, drawn before her line arrives (Al-Noor, 31 Oct 2026: "show the
     cards right away") — a find or a ranking only, already in their final
     order, so her finished answer lands on top and nothing moves */
  const [early, setEarly] = useState([]);
  /* her words as they arrive — kept, not drawn (see ONE FINISHED ANSWER below) */
  const streamed = useRef('');
  /* ⚠️ SIX SECONDS. Long enough that an ordinary answer never trips it, short
     enough to reach him before he reaches for the back button. Cleared with
     `busy`, so it can never be left on over a finished answer. */
  const signedIn = !!getToken();
  const endRef = useRef(null);
  /* ⚠️ THE TOP OF THE NEWEST THING SHE SAID — see the scroll note below. */
  const lastTurnRef = useRef(null);
  /* ⚠️ THE NEWEST QUESTION — Google's way (Al-Noor, 31 Oct 2026): a new
     question is brought to the top of the screen and her answer reads down
     under it; the ask bar stays pinned at the bottom. */
  const lastAskRef = useRef(null);
  const asking = useRef(false);
  /* "New chat" — the next question starts a new conversation on the server */
  const freshRef = useRef(false);
  function newChat() {
    setTurns([]); setQ(''); setFound(null); freshRef.current = true;
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { window.scrollTo(0, 0); }
  }

  /* ── HER VOICE ─────────────────────────────────────────────────────────
   *
   * ⚠️ BOTH SWITCHES COME FROM THE SERVER AND ARE OFF UNTIL THEY ARRIVE.
   * Super Admin owns them (migration 092) and this screen never assumes. A
   * microphone that appears and then vanishes is worse than one that appears
   * a moment late.
   *
   * ⚠️ AND THE BUTTON IS HIDDEN WHEN THE PHONE CANNOT DO IT. `micAvailable()`
   * asks the browser rather than the switch — a button that is on for Eleat
   * and impossible on this handset is a button that does nothing. */
  /* ⚠️ THE MICROPHONE'S WIRING MOVED TO `useSpeech` IN ui.jsx ON 24 SEPTEMBER
     2026, because Home now carries the same button and the same bar. What the
     words MEAN is still this screen's — here they are a question for her; on
     Home they are a search. Only the plumbing is shared.
     ⚠️ NOT COUNTED HERE. `ask()` already fires zaryna_query, so counting it on
     the microphone too made every spoken question two questions in the
     analytics — and a microphone somebody taps and says nothing into is not a
     question at all. */
  const speech = useSpeech({
    onPartial: (t) => setQ(t),
    onDone: (t) => { if (t) ask(t, { spoken: true }); },
    /* ⚠️ SILENCE HAS TO SAY SOMETHING. `no-speech` used to be swallowed with
       no message at all, so a second tap after a failure did nothing, showed
       nothing and answered nothing — Al: *"even pressing on it again, it said
       nothing and it didn't answer."*  Still not an error and still not red. */
    onQuiet: () => setTurns((x) => [...x,
      { who: 'z', text: "I didn't catch that. Try again, or type it." }]),
    onError: (e) => setTurns((x) => [...x, { who: 'z', error: e.message }]),
  });
  const flags = speech.flags;
  const hearing = speech.hearing;
  const [callState, setCallState] = useState(null); // null | connecting | listening | speaking
  const liveCall = useRef(null);

  /* ── THE ROTATING EXAMPLE ──────────────────────────────────────────────
   * `which` turns every 3.2s; `focused` and a non-empty box hide it; `used`
   * hides it for good. All four conditions come from the locked script's own
   * `paint()`, which is the only place this behaviour has ever been written
   * down — this is a port of it, not a new idea. */
  const [which, setWhich] = useState(0);
  const [focused, setFocused] = useState(false);
  const [used, setUsed] = useState(false);
  const showExample = !used && !focused && !q && !hearing && !callState;


  useEffect(() => {
    if (!showExample) return undefined;
    const t = setInterval(() => setWhich((i) => i + 1), EXAMPLE_TURN_MS);
    return () => clearInterval(t);
  }, [showExample]);

  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ THE ANSWER LANDED IN THE MIDDLE OF THE RESTAURANT CARDS.

     Al, 27 September 2026: *"when it finished typing, it went into the middle
     of the page of the restaurants, missed out all the things on top and
     everything else. So it should start off at the top so people can scroll
     down, not in the middle."*

     ⚠️ THIS SCROLLED TO `block: 'end'` — THE BOTTOM OF EVERYTHING. An answer
     is not one bubble: it is her words **plus up to eight venue cards**. So
     "scroll to the end" put him at the bottom of the eighth card, with her
     entire answer and the first seven cards above the fold. **He never saw the
     reply he had waited for.**

     ⚠️ AND IT WAS RIGHT FOR THE SCREEN IT WAS WRITTEN FOR. Bottom-anchoring is
     correct for a chat of short messages, which is what this was before the
     cards came back with the answer on 23 September. **The cards changed what
     "the end" means and nothing revisited this line.**

     ⚠️ SO IT ANCHORS THE TOP OF HER NEWEST TURN, which is what ChatGPT and
     Gemini both do with a long reply — the answer begins at the top of the
     screen and you read downward into the cards. `block: 'start'`.

     ⚠️ AND WHILE SHE IS STILL WORKING IT KEEPS THE WAITING BUBBLE IN VIEW
     instead, because a "typing…" you cannot see is the same complaint one step
     earlier. */
  const askedCount = turns.filter((t) => t.who === 'me').length;
  useEffect(() => {
    if (!askedCount) return;
    const el = lastAskRef.current;
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [askedCount]);
  useEffect(() => { if (!busy) freshRef.current = false; }, [busy]);

  /* ⚠️ HANG UP ON THE WAY OUT. Leaving this screen with a live conversation
   * running would keep a metered socket open and a microphone hot behind a
   * screen nobody is looking at. (`useSpeech` stops the microphone itself.) */
  useEffect(() => () => {
    if (liveCall.current) liveCall.current.hangUp();
  }, []);

  /* Stage 2 — the real conversation. Only ever reachable when Al has turned
   * it on; the server refuses to mint a token otherwise. */
  async function tapCall() {
    if (liveCall.current) { liveCall.current.hangUp(); liveCall.current = null; setCallState(null); return; }
    try {
      liveCall.current = await startConversation({
        onState: (s) => setCallState(s === 'ended' ? null : s),
        onHeard: (t) => setTurns((x) => [...x, { who: 'me', text: t, spoken: true }]),
        onSaid: (t) => setTurns((x) => [...x, { who: 'z', text: t, spoken: true }]),
        /* ⚠️ THIS IS WHAT PUTS A SPOKEN CONVERSATION INTO HER MEMORY, and
           until an audit looked for it, nothing did — the transcripts landed
           in React state and died when the screen closed, while two files
           said they became part of the thread. A failure here loses one turn
           and must never interrupt somebody mid-sentence, so it is caught and
           dropped rather than shown. */
        onTurn: (t) => {
          call('/zaryna/voice/turn', { method: 'POST', auth: true, body: t })
            .catch(() => {});
        },
        /* ⚠️ THIS IS HOW A SPEAKING ZARYNA KNOWS ANY RESTAURANT AT ALL.
           She calls `eleat_lookup` mid-sentence; Google tells this browser;
           this asks Eleat and the answer goes straight back into the
           conversation. The market is OURS, never hers — she cannot widen her
           own reach past the city this screen is in. */
        onLookup: (args) => call('/zaryna/voice/lookup', {
          method: 'POST', auth: true,
          body: {
            market: marketNow(),
            ...whereTheyAre(),
            area: args.area || undefined,
            cuisine: args.cuisine || undefined,
            deal_type: args.deal_type || undefined,
            open_now: args.open_now === true ? true : undefined,
          },
        }),
        onError: (e) => {
          setCallState(null); liveCall.current = null;
          setTurns((x) => [...x, { who: 'z', error: e.message }]);
        },
      });
    } catch (e) {
      setCallState(null); liveCall.current = null;
      setTurns((x) => [...x, { who: 'z', error: e.message }]);
    }
  }

  async function ask(text, opts = {}) {
    if (asking.current) return undefined;
    asking.current = true;
    try { return await askOnce(text, opts); } finally { asking.current = false; }
  }
  async function askOnce(text, opts = {}) {
    stopSpeaking();   // a new question silences the last answer
    const message = String(text || '').trim();
    /* ⚠️ ONE ASK AT A TIME — Al-Noor's Dutch question came back twice (the same
       line and card, two times): `busy` is state, so a second tap in the same
       moment got past it. A ref is set at once. */
    if (!message || busy) return;
    setQ('');
    /* after a city is chosen the question is already on screen — not twice */
    if (!opts.again) setTurns((t) => [...t, { who: 'me', text: message }]);
    /* ══════════════════════════════════════════════════════════════════
       ⚠️ SHE HAS NOTHING TO SEARCH WHERE ELEAT IS NOT, AND SHE SAYS SO.

       Two reasons, and the second is the one that would have bitten.
       ① The concierge prompt has forbidden inventing a restaurant since
          August, so a Zaryna with no market has nothing honest to offer.
       ② `POST /zaryna/voice/lookup` validates `market: z.string().max(60)`
          — a null is a **422**, and this screen would have shown the
          Eleater a validation error in a chat bubble. Raised and closed
          inside this work rather than at sign-off (RULE 7).

       The words are the Location screen's own — RULE 4, nothing invented. */
    if (!marketNow()) {
      setTurns((t) => [...t, { who: 'z', text: "Eleat isn't in your location yet." }]);
      return;
    }
    /* ⚠️ OUTSIDE ELEAT'S CITIES, THEY CHOOSE — Al-Noor, 16 Oct 2026: "No city
       is picked. Zaryna says Eleat isn't in X city yet. Choose a city with the
       buttons… if we add Vancouver, Edmonton, Macau, Singapore, London… it's
       theirs to choose. People aren't stupid." (Replaces 15 Oct's "Showing the
       nearest city".) The buttons are /discovery/markets, so a new city appears
       by itself; a tap chooses it the way Home's city buttons do, then she
       answers the question they asked. With no location at all, the same
       buttons without the first sentence. */
    const where = browseMarket(readCentre());
    if (where.fallback || where.unknown) {
      const d = await call('/discovery/markets').catch(() => null);
      const cities = (d && Array.isArray(d.markets)) ? d.markets : [];
      /* ⚠️ THE CITY IN THEIR OWN WORDS IS THEIR CHOICE — Al-Noor, 31 Oct 2026:
         "ik ben nu in Hongkong…" and she still asked "which city". A question
         that names one of Eleat's cities chooses it, exactly as the button. */
      const named = cityInWords(message, cities);
      if (named) {
        writeCentre(centreFrom({
          label: named.name_en || named.code, market: named.code,
          lat: Number(named.centre_lat), lng: Number(named.centre_lng), country_code: named.country,
        }, { from: 'chosen' }));
        setTurns((x) => x.map((y) => (y.cities && y.cities.length ? { ...y, cities: [] } : y)));
        return askOnce(message, { ...opts, again: true });
      }
      /* Al-Noor, 16 Oct 2026: no "sorry", no city named — "they could be
         sitting on a farm" */
      const which = where.fallback
        ? "Eleat isn't in your location yet. Which city can I help you with?"
        : 'Which city can I help you with?';
      /* ⚠️ A SPOKEN QUESTION STAYS SPOKEN THROUGH THE CITY CHOICE — Al-Noor,
         30 Oct 2026: asked on the microphone, she answered in writing only,
         because the re-ask after the city tap dropped `spoken`. She says the
         city question out loud too. */
      setTurns((t) => [...t, { who: 'z', cities, pending: message, pendingSpoken: !!opts.spoken, text: which }]);
      if (opts.spoken) speakAnswer(which, marketNow());
      return;
    }
    setBusy(true); setFound(null); foundRef.current = null; setEarly([]);
    try {
      /* ⚠️ COUNTED AS IT IS ASKED, not when she answers. A question she
         could not answer is still a question somebody asked Eleat, and it is
         the one Eleat most needs to know about. The registry requires no
         fields for this event, deliberately — the question itself is not
         stored here. */
      track('zaryna_query', { source_screen: 'zaryna' });

      /* ══════════════════════════════════════════════════════════════════
         ⚠️⚠️ HER WORDS ARRIVE AS SHE WRITES THEM — 27 September 2026.

         Al: *"it took about 15 seconds for Zaryna to answer… It's got to be
         quick, quick, quick, quick, like I snap my fingers quick."*  And on
         the waiting bubble: *"the typing looks like it's actually stuck."*

         ⚠️ THE ANSWER WAS NEVER A FASTER MODEL. `/zaryna/chat` waits for
         every word to be written and then hands over a finished paragraph, so
         a fifteen-second answer is fifteen seconds of an empty screen.
         `/zaryna/chat/stream` sends each piece as it is written: **the first
         words land in about a second whatever the whole answer costs.**

         ⚠️ THE CARDS STILL COME AT THE END, and that is not a compromise —
         `concierge()` picks them by searching her finished text for
         restaurant names, so they are not knowable until she stops.

         ⚠️ AND IT FALLS BACK TO `/zaryna/chat` IF THE STREAM WILL NOT RUN.
         This session could not build or test the app, and this is her only
         working path — a proxy that buffers, a browser without streaming, or
         a server mid-deploy must not cost an Eleater their question. **A
         transport fallback, not two versions of a product decision.** */
      const body = {
        message, market: marketNow(), spoken: !!opts.spoken, fresh: freshRef.current,
        lang: myLanguage(), ...whereTheyAre(),
      };

      /* ══════════════════════════════════════════════════════════════════
         ⚠️ ONE FINISHED ANSWER, NOTHING MOVES — Al-Noor, 15 Oct 2026: "the
         restaurant summary came first then the restaurant page… why are we
         doing it this way… why is it not clean like Google, Siri, Muse." The
         screen changed three times: the found places, then her words alone
         (the places gone), then the places back under her lines. Measured
         live: her first word and her finished answer arrive 0.1 s apart, so
         drawing the words as they came only made the jump. Now: her Z and
         steps with the places she found, then her finished answer — one line,
         its card — drawn once. The words are still read as they come (kept
         in `streamed`) so a broken connection keeps what arrived. */
      streamed.current = '';
      const d = await askStreaming(body, (piece) => {
        streamed.current += piece;
      }, (f) => { foundRef.current = f; setFound(f); },
      (list) => { if (Array.isArray(list)) setEarly(list); }) || await call('/zaryna/chat', {
        method: 'POST', auth: true,
        /* ⚠️ `spoken` IS CARRIED SO THE TRANSCRIPT KNOWS. A turn that was
           said out loud and a turn that was typed both belong to the same
           thread (migration 093) — but which it was decides how she answers,
           and anyone reading a conversation back needs to see it. */
        /* ⚠️ `market` IS THE CENTRE'S NOW — see the block at the top of this
           file for the comment that used to sit here and why it was wrong. */
        /* ⚠️ `lang` WAS ON THE ROUTE SINCE AUGUST AND NO SCREEN EVER SENT IT.
           `zaryna.routes.js` reads `req.body.lang`, and this body did not have
           the field — so it was `undefined` on every question an Eleater has
           ever asked, and the transcript recorded no locale at all.

           ⚠️ IT DOES NOT DECIDE HOW SHE REPLIES, and that is deliberate. Her
           persona already says *"You are fully MULTILINGUAL — always reply in
           the user's language"*, so a question typed in Portuguese comes back
           in Portuguese whatever is stored. Al, 24 September: *"if it's
           Arabic, it's in Arabic. Portuguese, it's in Portuguese."*  Forcing
           the stored language over the one they just typed would break exactly
           that. This tells her which language they CHOSE — for the transcript,
           and for the two places the server already uses it. */
        body: {
          message, market: marketNow(), spoken: !!opts.spoken, fresh: freshRef.current,
          lang: myLanguage(), ...whereTheyAre(),
        },
      });

      /* ⚠️ THE PIECES ARE REPLACED BY THE FINISHED ANSWER, NOT ADDED TO.
         The streamed turn was a scratchpad; `d.reply` is what the server
         actually recorded, and keeping both would show the answer twice.

         ⚠️⚠️ BUT ONLY IF THERE IS A FINISHED ANSWER TO REPLACE IT WITH. If
         the connection broke part-way, `d.broke` is set and `d.reply` is
         null — and removing the scratchpad then would **delete words the
         Eleater has already read**, leaving a question with no answer under
         it and nothing saying why. Caught by reading this back rather than by
         anything that ran. */
      if (d && !d.reply && d.broke) {
        /* keep what arrived, and stop pretending more is coming */
        setTurns((t) => [...t, streamed.current
          ? { who: 'z', text: streamed.current }
          : { who: 'z', error: 'Zaryna can\'t answer right now. Please try again in a moment.' }]);
        setBusy(false);
        return;
      }

      /* ⚠️ THE THREE SHAPES, EACH GIVEN ITS OWN TURN. */
      if (d.needs === 'location_choice') {
        setTurns((t) => [...t, {
          who: 'z', text: d.question, options: d.options || [],
        }]);
      } else if (d.needs === 'market') {
        setTurns((t) => [...t, { who: 'z', text: d.question }]);
      } else if (d.signup) {
        /* a visitor's 4th question today → the join sheet (Al-Noor, 22 Oct 2026);
           their question stays on screen to ask again once they are in */
        needsAccount('ask', { ret: hereNow() });
      } else if (d.reply) {
        const turn = {
          who: 'z', text: d.reply, model: d.model, connected: d.connected,
          /* ⚠️ THE CARDS ARE THE ANSWER, NOT DECORATION. Al, 23 September:
             "the card is there, and there's several cards there. Why should
             she answer all these other questions?" She says the one line that
             makes a place worth a tap; the card carries the offer, the prices,
             the hours and the photographs, on the page built to show them. */
          cards: d.cards || [],
          /* every other place Eleat found that she did not name — Al-Noor, 13 Oct:
             she named 2 of the 4 places with sea bass; every match shows */
          /* …only for a find question (16 Oct 2026): the places found for "a date
             spot" are just nearby places, not matches, and an unexplained row of
             them under her picks is the clutter he asked to remove */
          more: ((foundRef.current && foundRef.current.specific && foundRef.current.cards) || [])
            .filter((c) => !(d.cards || []).some((x) => x.id === c.id)),
        };
        setTurns((t) => [...t, turn]);
        /* asked with the microphone → she answers out loud too (Al-Noor, 22 Oct 2026) */
        if (opts.spoken) speakAnswer(spokenPart(turn), marketNow());
      } else {
        /* ⚠️ A SHAPE THIS SCREEN DOES NOT KNOW IS SAID OUT LOUD, not swallowed
           into an empty bubble. Three failures today were a screen quietly
           rendering something it could not read. */
        setTurns((t) => [...t, {
          who: 'z', error: 'Zaryna answered in a way this screen could not read.',
        }]);
      }
    } catch (e) {
      setTurns((t) => [...t, { who: 'z', error: e.message }]);
    } finally {
      setBusy(false);
    }
  }

  /* ⚠️ THE SEARCH BOX HANDS ITS QUESTION STRAIGHT TO HER — `08f_Zaryna.html`
   * script `zaryna-inbound`. Al: *"connect that search — a dish, cuisine,
   * restaurant, all that — to Zaryna. We don't have to tell anybody that it's
   * Zaryna there."*  So somebody types "wings" on Home and arrives here with
   * the question already ASKED, not merely typed into the box.
   *
   * ⚠️ `near` IS JOINED IN THE WORDS, not passed as a field, because that is
   * what the locked script does: "wings" means nothing without "near where",
   * and she reads the sentence. It runs once — the ref, not the URL, is what
   * stops a re-render asking the same question twice. */
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return;
    const p = new URLSearchParams(window.location.search);
    const text = (p.get('q') || '').trim();
    if (!text) return;
    const near = (p.get('near') || '').trim();
    asked.current = true;
    setUsed(true);
    /* asked with Home's or Explore's microphone → she answers out loud here too */
    ask(near ? `${text} near ${near}` : text, { spoken: p.get('spoken') === '1' });
  }, []);

  /* ⚠️ NO SIGN-IN WALL — Al-Noor, 11 Oct 2026: "Zaryna should be everywhere. Signed
     in or not." A visitor is answered too (the server counts a visitor's
     questions per phone, 189); only an Eleater's conversation is remembered. */

  return (
    <div className="home zchat">
      {/* ══════════════════════════════════════════════════════════════════
          ⚠️ HER OWN HEADER — `08f_Zaryna.html .zav` + `.ztitle`
          Al, 23 September 2026: *"zaryna looks like shit. you did not follow
          her html."*  He is right: this was a back arrow and the word
          "Zaryna" in 22px hero type. The locked screen has a 40px
          persimmon-ringed circle, her name at 16px, and underneath it
          **"Your Eleat Concierge"** — the line that says what she is.

          ⚠️⚠️ AND THE CIRCLE NO LONGER HOLDS HER PHOTOGRAPH. Al, 24 September
          2026: *"we dont need a zaryna profile pic and because google and siri
          dont have one. so why should we."*  A DELIBERATE DEPARTURE from
          `08f_Zaryna.html`, which is `<img src="zaryna.jpg">` here — his
          explicit current instruction is precedence #1, above the locked
          file. Her NAME stays.
          ⚠️⚠️ AND ON 27 SEPTEMBER THE CIRCLE ITSELF WENT. A mark replaced the
          photograph and Al could not read the mark either — *"I don't know what
          it is… no one else is going to know what the hell it is either."*  So
          this header is her name and her line, and nothing else. The pulse that
          used to live here has moved to where it is actually needed: the
          waiting state below, which is the thing that looked stuck. */}
      {/* ⚠️ BACK GOES TO PROFILE, NOT HOME. `08f_Zaryna.html` line 87:
          `<a class="back" href="08_Profile.html">`. Al, 23 September, on the
          same fault one screen over: *"bell on homepage goes to notifications
          and then the back button goes to profile."*  Back means where this
          screen is reached FROM, and Zaryna is reached from Profile. */}
      <div className="top ztop">
        {/* asked from Home's search (Al-Noor, 13 Oct: answers open here) → back to Home */}
        {/* …and from Explore's (15 Oct) → back to Explore's list */}
        {new URLSearchParams(window.location.search).get('from') === 'home'
          ? <a className="back" href="?" aria-label="Back" onClick={(e) => { e.preventDefault(); goBack('?'); }}>‹</a>
          : new URLSearchParams(window.location.search).get('from') === 'explore'
            ? <a className="back" href="?explore=1" aria-label="Back" onClick={(e) => { e.preventDefault(); goBack('?explore=1'); }}>‹</a>
            : <a className="back" href="?profile=1" aria-label="Profile">‹</a>}
        {/* her mark beside her name — still, and speaking while she listens
            or answers (Al, 1 Oct 2026: one mark everywhere) */}
        {/* her face beside her name (Al-Noor, 14 Oct 2026 — his mother's
            likeness; never removed again). What she is doing now shows in
            the conversation itself, under the question (31 Oct 2026). */}
        <span className="zav"><ZarynaFace working={hearing || busy} /></span>
        <div className="ztitle">
          <div className="n">Zaryna</div>
          <div className="s">Your Eleat Concierge</div>
        </div>
        {turns.length > 0 && (
          <button type="button" className="znew" onClick={newChat} disabled={busy}>New chat</button>
        )}
      </div>

      {/* The ask bar was at the top from 24 Sep to 31 Oct 2026; it is now
          pinned at the bottom (see .zdock below). `➤` stays the send button —
          `›` means "opens" everywhere else in Eleat. */}
      <main className="zbody">
        {/* ⚠️⚠️ SHE OPENS THE CONVERSATION, IN HER OWN VOICE AND IN HER OWN
            BUBBLE. This was a grey line of instructions — *"Ask her anything
            about where to eat or drink"* — which is the app talking ABOUT her
            rather than her talking to somebody. `08f_Zaryna.html` puts a real
            `.msg.z` bubble on the screen before anybody types, and the words
            below are hers, quoted from it.
            ⚠️ AND "anything" WAS THE WRONG WORD TWICE OVER — the locked file's
            own comment forbids it, because she answers about Eleat's
            restaurants and nothing else. */}
        {/* ⚠️ HER GREETING IS ALWAYS THERE — it is the first `.msg.z` inside
            `.chat` in the locked file and it never leaves. It was conditional
            on an empty conversation, so the moment somebody asked anything
            the opening line vanished and the thread began with THEM. */}
        <div className="msg z">
          Hi. What are you looking for? Tell me what you’re in the mood for or
          what you’re planning, and I’ll help you find the right place.
        </div>

        {/* ⚠️⚠️ THE ROTATING EXAMPLE — the thing Al said was missing.
            Tapping it ASKS it. The locked script does `send.click()`, not
            `el.focus()`, and the difference is the whole reason it is a button
            rather than a placeholder. */}
        {showExample && !busy && (
          /* ⚠️ tx(): on a Chinese screen the question is sent in the words
             the person read, not the English source (audit, 7 Oct 2026) */
          <button type="button" className="ztry"
            onClick={() => { setUsed(true); ask(tx(EXAMPLES[which % EXAMPLES.length])); }}>
            {EXAMPLES[which % EXAMPLES.length]}
            <span className="zt-go">Ask</span>
          </button>
        )}

        {/* ⚠️ THE FOUR PROMPTS DO NOT VANISH AFTER THE FIRST QUESTION. In the
            locked file `.sugs` is a sibling of `.chat`, outside it, permanently
            on screen — they are a standing way to start a new line of enquiry,
            not a splash panel. They were conditional on `turns.length === 0`. */}
        <div className="sugs">
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" className="sug"
              onClick={() => { setUsed(true); ask(tx(s)); }}>{s}</button>
          ))}
        </div>

        {/* ⚠️ THE REF GOES ON HER LAST TURN, not on every turn and not on the
            container — that one element is what the scroll effect above
            anchors to, so the answer begins at the top of the screen instead
            of the reader landing at the bottom of the eighth card. */}
        {turns.map((t, i) => (
          <div key={i} className={t.who === 'me' ? 'zwrap u' : 'zwrap z'}
            ref={t.who === 'me' && i === turns.map((x) => x.who).lastIndexOf('me') ? lastAskRef : null}>
            {t.error
              ? <p className="err">{t.error}</p>
              : (t.who === 'z' && !t.streaming && ((t.cards && t.cards.length) || (t.more && t.more.length)))
                ? <AnswerPairs text={t.text} cards={t.cards || []} more={t.more || []} />
                : <div className={t.who === 'me' ? 'msg u' : 'msg z'}>{t.who === 'me' ? t.text : plainAnswer(t.text)}</div>}
            {t.who === 'z' && !t.error && !t.streaming && t.text && <SpeakButton text={spokenPart(t)} market={marketNow()} />}

            {/* ⚠️ THE SAME CARD THE RAILS AND EXPLORE USE — `VenueCard` from
                ui.jsx, not a second one drawn here. A Zaryna card that looked
                or behaved differently from a Home card would be two ideas of
                what a restaurant looks like, and this repository has paid for
                that shape of fault more than once. It also means her card
                already obeys the five-review rating rule and opens the venue
                at the right tab. */}
            {/* Al-Noor, 10 Oct 2026: the page is the answer — printed here, so it
                costs nothing and she does not have to say it every time */}
{/* ⚠️ NO "Tap a restaurant…" LINE — Al-Noor, 21 Oct 2026: Eleat does not
                explain itself to Eleaters ("are we babying our Eleaters"). */}

            {/* the cities Eleat is in — a tap chooses one and she answers */}
            {t.cities && t.cities.length > 0 && (
              <div className="chipbox">
                {t.cities.map((m) => {
                  const name = zh(m.name_en || m.name, m.name_zh) || m.code;
                  return (
                    <button key={m.code} type="button" className="chip"
                      onClick={() => {
                        writeCentre(centreFrom({
                          label: name, market: m.code,
                          lat: Number(m.centre_lat), lng: Number(m.centre_lng),
                          country_code: m.country,
                        }, { from: 'chosen' }));
                        setTurns((x) => x.map((y) => (y === t ? { ...y, cities: [] } : y)));
                        ask(t.pending, { again: true, spoken: !!t.pendingSpoken });
                      }}>{name}</button>
                  );
                })}
              </div>
            )}

            {/* ⚠️ AMBIGUOUS MEANS ASK — the options are hers, not a guess. */}
            {t.options && t.options.length > 0 && (
              <div className="chipbox">
                {t.options.map((o) => (
                  <button key={o.id} type="button" className="chip"
                    onClick={() => ask(o.name)}>{o.name}</button>
                ))}
              </div>
            )}

            {/* ⚠️ WHEN SHE IS NOT CONNECTED, THE SCREEN SAYS SO. `connected:
                false` means no model answered and the text is a fallback;
                presenting that as Zaryna's own recommendation would be
                putting words in her mouth. */}
            {t.who === 'z' && t.connected === false && (
              <p className="mut">Zaryna is not connected right now.</p>
            )}
          </div>
        ))}

        {/* ⚠️ WAITING IS A BUBBLE OF HERS, NOT A GREY LINE UNDERNEATH.
            `08f_Zaryna.html` line 174: `var typing=add('typing…','z');
            typing.style.opacity='.6';` — it is added to the stream as a real
            `.msg.z` and REPLACED by the answer in the same place. A caption
            below the conversation reads as the app reporting on her; a bubble
            reads as her about to speak, which is what is happening. */}
        {/* ══════════════════════════════════════════════════════════════
            ⚠️⚠️ "typing…" SAT THERE AND LOOKED STUCK.

            Al, 27 September 2026: *"it just says typing, typing, typing,
            typing… it's taking a long time… when it's taking time, I feel like
            hitting the back button or resetting it because it's taking too
            fucking long, so there's no indication apart from typing and the
            typing looks like it's actually stuck."*

            ⚠️⚠️ AND THE WORD CHANGED TOO — 27 September 2026, ON RESEARCH AL
            ASKED FOR. `08f_Zaryna.html` line 174 is `var typing=add('typing…',
            'z')`, so "typing…" was the locked screen's word and it was kept on
            RULE 4 grounds. Al then asked the right question — *"instead of
            typing, isn't a better word called answering? What is the best
            practice?"* — and the answer is that **she is not typing**. It
            promises the speed of a person at a keyboard, and none of ChatGPT,
            Gemini or Claude use it; they show a moving shape, or stream the
            words and need no label at all.

            ⚠️ "Looking…" BECAUSE IT IS LITERALLY WHAT SHE IS DOING — running
            the Eleat search before she writes anything. It sets the right
            expectation instead of a false one.

            ⚠️⚠️ AND THE RESEARCH SAYS THE INDICATOR ITSELF WAS MAKING IT WORSE:
            a typing indicator running 6–8 seconds reads as *"the system is
            slow"* and produces **40% more abandonment than showing nothing at
            all**. Al's ran fifteen seconds and he said he wanted to refresh the
            page. **He was the normal reaction, not a fussy one.** The permanent
            answer is streaming, which removes the label entirely.

            ⚠️ What it never had was MOVEMENT. A caption that does not change for eight seconds is
            indistinguishable from a screen that has died, and the honest
            reading of his message is that he was about to reload and lose the
            answer that was on its way.

            ⚠️ AND THE WAIT IS REAL, NOT A BUG TO HIDE. She runs the whole Eleat
            search, builds the brief for up to 25 venues and then writes — and
            on Render's free tier the first request after an idle spell also has
            to wake the service. `api.js` allows 45 seconds for exactly that
            reason. **So the fix is to make the waiting legible, not to pretend
            it is quick.**

            ⚠️ IT SAYS MORE AS IT GOES, which is the whole point: the dots move
            from the first frame, and past six seconds it says so in her own
            plain voice rather than leaving him guessing. No progress bar — a
            bar that cannot know its own percentage is a lie drawn as a fact. */}
        {/* ⚠️ ONE CLEAN LINE WHILE SHE WORKS — Al-Noor, 31 Oct 2026: "too tight
            and looks messy and cluttered… you see how clean yours is" (Claude's
            own: one small mark and one word, room around it). Her face, gently
            pulsing, and what she is doing — no bubble, no second spinner. */}
        {busy && (
          <div className="zthink" role="status">
            <ZarynaFace working />
            <ZarynaSteps question={(turns.filter((t) => t.who === 'me').slice(-1)[0] || {}).text} found={found} />
          </div>
        )}
        {busy && early.length > 0 && (
          <div className="zcards">
            {early.map((v) => <BookingCard key={v.id} v={v} tab={v.tab} reason={null} />)}
          </div>
        )}
        {/* room under the newest answer, so its question can sit at the top of
            the screen however short the answer is (as ChatGPT and Gemini do) */}
        {turns.length > 0 && <div className="zroom" aria-hidden="true" />}
        <div ref={endRef} />
      </main>

      {/* ⚠️⚠️ THE ASK BAR IS PINNED AT THE BOTTOM — Al-Noor, 31 Oct 2026:
          "Google's way". It sat at the top (24 Sep) and scrolled away, so after
          a few answers he had to scroll all the way back up to ask again. Now
          it never leaves the screen, just above the tab bar, as in Google's AI
          Mode, Gemini and ChatGPT. */}
      <div className="zdock">
        <SearchBar className="zask"
          value={q} onChange={setQ} onSubmit={() => ask(q)}
          placeholder="Search Eleat…"
          goLabel="Ask Zaryna"
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          inputDisabled={busy || hearing || !!callState}
          mic={speech.allowed
            ? { on: hearing, onTap: speech.tap, disabled: busy || !!callState,
              label: 'Speak to Zaryna' }
            : null}
          after={(
            <>
              {/* Stage 2 — the real conversation. Only when Al has turned it on. */}
              {flags.live.enabled && (
                <button type="button" className={callState ? 'zcallbtn on' : 'zcallbtn'}
                  onClick={tapCall} disabled={busy || hearing}
                  aria-label={callState ? 'End the conversation' : 'Talk to Zaryna'}
                  aria-pressed={!!callState}>
                  {/* ⚠️ A WAVEFORM, NOT A TELEPHONE — see the note beside
                      `Waveform` in ui.jsx. This was `☏`, a rotary handset, for
                      something that is not a call and touches no telephone
                      network. ChatGPT and Gemini both use a microphone for
                      dictation and a waveform for a spoken conversation, which
                      is exactly the pair sitting side by side here. */}
                  {callState ? '■' : <Waveform />}
                </button>
              )}
              {/* ➤ is the shared bar's now (mic while empty, ➤ once typed) —
                  Al-Noor, 5 Nov 2026: one bar everywhere */}
            </>
          )} />
        <MicStatus speech={speech} />

        {/* ⚠️ THE CALL SAYS WHAT IT IS DOING, ALWAYS. A live conversation with
            no visible state is somebody holding a phone wondering whether they
            have been hung up on — the worst failure a concierge can have. */}
        {callState && (
          <p className="mut zcall">
            {callState === 'connecting' && 'Connecting to Zaryna…'}
            {callState === 'listening' && 'Zaryna is listening — just talk.'}
            {callState === 'speaking' && 'Zaryna is speaking…'}
          </p>
        )}
      </div>

      <TabBar active="zaryna" />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * HER LINE, THEN THE CARDS — Al-Noor, 16 Oct 2026: "she describes describes
 * describes five restaurants, then she lists five restaurants. That shouldn't
 * happen… these are the five restaurants that have chicken wings tonight."
 * Her lines that name no place come first (and her closing question last);
 * a line she wrote about one place is drawn UNDER that place's card, so a
 * place is never said twice. A find question has no such lines — the cards,
 * each with its matching dish and price, are the answer. Places she did not
 * name come after, so every match Eleat found is on screen.
 * ══════════════════════════════════════════════════════════════════════════ */
function splitAnswer(text, cards, more) {
  const paras = plainAnswer(text).split(/\n+/).map((x) => x.trim()).filter(Boolean);
  const all = [...cards, ...more.filter((m) => !cards.some((c) => c.id === m.id))];
  const names = (c) => [c.name, c.name_zh].filter(Boolean).map((n) => String(n));
  const reason = new Map();
  const top = []; const bottom = [];
  let seenPlace = false;
  paras.forEach((p) => {
    const low = p.toLowerCase();
    const mine = all.filter((c) => names(c).some((n) => low.includes(n.toLowerCase())));
    if (mine.length === 1 && !reason.has(mine[0].id)) {
      /* the line without the name it opens with ("Ho Lee Fook — …") */
      let r = p;
      for (const n of names(mine[0])) {
        if (r.toLowerCase().startsWith(n.toLowerCase())) { r = r.slice(n.length).replace(/^[\s—–:,.\-]+/, ''); break; }
      }
      reason.set(mine[0].id, r || p);
      seenPlace = true;
    } else {
      (seenPlace ? bottom : top).push(p);
    }
  });
  return { all, reason, top, bottom };
}

/* ⚠️ WHAT SHE SAYS OUT LOUD IS SHORTER THAN WHAT IS SHOWN — Al-Noor, 22 Oct
   2026 ("the least amount she speaks… here, take a look at the list"), and
   Google's own voice-design rule: a list is never read out item by item. Her
   lines that name no place (her opening line, her question) are spoken; each
   place's own line stays written under its card. */
function spokenPart(t) {
  const cards = t.cards || []; const more = t.more || [];
  if (!cards.length && !more.length) return t.text;
  const { top, bottom } = splitAnswer(t.text, cards, more);
  return [...top, ...bottom].join(' ') || null;
}

function AnswerPairs({ text, cards, more }) {
  const { all, reason, top, bottom } = splitAnswer(text, cards, more);
  return (
    <>
      {top.map((p, i) => <div key={'t' + i} className="msg z">{p}</div>)}
      {all.length > 0 && (
        <div className="zcards">
          {all.map((v) => <BookingCard key={v.id} v={v} tab={v.tab} reason={reason.get(v.id) || null} />)}
        </div>
      )}
      {bottom.map((p, i) => <div key={'b' + i} className="msg z">{p}</div>)}
    </>
  );
}
